import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Pagination, usePagination } from "../components/Pagination";
import { Plus, PencilSimple, Package, MagnifyingGlass, FunnelSimple } from "@phosphor-icons/react";

const UNITS = ["kg", "g", "l", "ml", "nos", "pcs", "bag", "box"];

export default function RawMaterials() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const syncRefs = async (rm) => {
    if (!window.confirm(`Sync "${rm.code} · ${rm.name}" across every inventory bag, PO line & batch that references it?`)) return;
    try {
      const r = await api.post(`/raw-materials/${rm.id}/sync-references`);
      toast.success(`Synced · inv=${r.data.inventory_updated} · docs=${r.data.documents_updated} · batches=${r.data.batches_updated}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sync references");
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/raw-materials");
      setItems(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load raw materials");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i =>
    !q || i.name.toLowerCase().includes(q.toLowerCase()) || (i.code || "").toLowerCase().includes(q.toLowerCase())
  );

  const { page, setPage, pageSize, setPageSize } = usePagination(20);
  useEffect(() => { setPage(1); }, [q, setPage]);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="p-6 lg:p-10 bg-zinc-50 min-h-screen" data-testid="raw-materials-page">
      <PageHeader
        label="MASTER DATA"
        title="Raw Materials"
        subtitle="Manage your inventory components. Barcodes are generated per bag automatically."
        actions={<Btn className="shadow-sm shadow-indigo-200" onClick={() => setOpen({})} data-testid="rm-new"><Plus size={16} /> New Material</Btn>}
      />

      <div className="relative mb-6 max-w-md">
        <MagnifyingGlass className="absolute left-3 top-2.5 text-zinc-400" size={18} />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search by name or code..."
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
          data-testid="rm-search"
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Package} title="No raw materials" subtitle="Start by adding your first material to the system." />
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/50 border-b border-zinc-100">
              <tr className="text-left text-zinc-500 uppercase tracking-wider text-[10px] font-bold">
                <th className="px-6 py-4">Code</th>
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Unit</th>
                <th className="px-6 py-4">Usage</th>
                <th className="px-6 py-4">Description</th>
                <th className="w-20 px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {paged.map(rm => (
                <tr key={rm.id} className="hover:bg-zinc-50/80 transition-colors" data-testid={`rm-${rm.code}`}>
                  <td className="px-6 py-4 font-mono font-semibold text-indigo-600">{rm.code}</td>
                  <td className="px-6 py-4 font-medium text-zinc-900">{rm.name}</td>
                  <td className="px-6 py-4"><Badge tone="zinc" className="rounded-full px-2.5 py-0.5">{rm.unit}</Badge></td>
                  <td className="px-6 py-4">
                    <Badge tone={rm.usage_type === "multiple" ? "orange" : "zinc"} className="rounded-full" data-testid={`rm-usage-${rm.code}`}>
                      {rm.usage_type === "multiple" ? "Multi-product" : "Single"}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-zinc-500 text-xs italic">{rm.description || "—"}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex gap-1 justify-end">
                      {isAdmin && (
                        <button
                          onClick={() => syncRefs(rm)}
                          className="p-2 text-zinc-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                          title="Sync this material's name/code to every bag, PO line and batch that references it"
                          data-testid={`rm-sync-${rm.code}`}
                        >
                          <Package size={18} weight="bold" />
                        </button>
                      )}
                      <button onClick={() => setOpen(rm)} className="p-2 text-zinc-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" data-testid={`rm-edit-${rm.code}`}>
                        <PencilSimple size={18} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 border-t border-zinc-100">
            <Pagination page={page} pageSize={pageSize} total={filtered.length} onPageChange={setPage} onPageSizeChange={setPageSize} testId="rm-pager" />
          </div>
        </div>
      )}

      {open && <RMModal existing={open.id ? open : null} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function RMModal({ existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [name, setName] = useState(existing?.name || "");
  const [code, setCode] = useState(existing?.code || "");
  const [unit, setUnit] = useState(existing?.unit || "kg");
  const [description, setDescription] = useState(existing?.description || "");
  const [usageType, setUsageType] = useState(existing?.usage_type || "single");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !code.trim()) { toast.error("Name and code are required"); return; }
    setBusy(true);
    try {
      const body = { name, code, unit, description };
      if (isAdmin) body.usage_type = usageType;
      if (isEdit) {
        await api.put(`/raw-materials/${existing.id}`, body);
      } else {
        await api.post("/raw-materials", body);
      }
      toast.success(isEdit ? "Updated" : "Added");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={isEdit ? `Edit ${existing.name}` : "New Raw Material"}>
      <form onSubmit={submit} className="space-y-5" data-testid="rm-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Code (3-letter uppercase)">
            <Input
              value={code}
              className="border-zinc-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 rounded-lg"
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. POT"
              maxLength={4}
              required
              data-testid="rm-code"
            />
          </Field>
          <Field label="Unit">
            <Select 
              value={unit} 
              className="border-zinc-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 rounded-lg"
              onChange={e => setUnit(e.target.value)} 
              data-testid="rm-unit"
            >
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </Select>
          </Field>
        </div>

        <Field label="Material Name">
          <Input 
            value={name} 
            className="border-zinc-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 rounded-lg"
            onChange={e => setName(e.target.value)} 
            placeholder="e.g. Russet Potatoes" 
            required 
            data-testid="rm-name" 
          />
        </Field>

        <Field label="Description (optional)">
          <Input 
            value={description} 
            className="border-zinc-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 rounded-lg"
            onChange={e => setDescription(e.target.value)} 
            placeholder="Additional notes" 
            data-testid="rm-desc" 
          />
        </Field>

        {isAdmin && (
          <div className="bg-zinc-50 p-4 rounded-xl border border-zinc-100">
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3">Usage Policy (Admin)</p>
            <div className="flex gap-6" data-testid="rm-usage-toggle">
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 cursor-pointer">
                <input
                  type="radio"
                  name="usage_type"
                  className="accent-indigo-600"
                  value="single"
                  checked={usageType === "single"}
                  onChange={() => setUsageType("single")}
                  data-testid="rm-usage-single"
                />
                Single-product
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 cursor-pointer">
                <input
                  type="radio"
                  name="usage_type"
                  className="accent-indigo-600"
                  value="multiple"
                  checked={usageType === "multiple"}
                  onChange={() => setUsageType("multiple")}
                  data-testid="rm-usage-multiple"
                />
                Multi-product
              </label>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-zinc-100">
          <Btn type="button" variant="secondary" className="hover:bg-zinc-100" onClick={onClose}>Cancel</Btn>
          <Btn 
            type="submit" 
            disabled={busy} 
            className="bg-indigo-600 hover:bg-indigo-700 shadow-md shadow-indigo-200"
            data-testid="rm-submit"
          >
            {busy ? "Saving..." : (isEdit ? "Update Material" : "Create Material")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}